import type { DevicePermissionReport, PermState } from '@shared/setup'
import { getBiometricAvailability, isBiometricUnlockEnabled } from '../../../services/biometric-auth-service'
import { getLocationAccuracyAuthorization } from '../../../services/background-location'
import {
  checkAndroidLocationPrecision,
  checkPermission,
  listApplicablePermissions,
  type PermissionKind,
} from '../../../services/permissions/permissions-service'
import {
  canUseFullScreenIntent,
  getIncomingCallsChannelStatus,
  getNativeVoipToken,
  isIgnoringBatteryOptimizations,
} from '../../../services/voice-call/native-call'
import { pushDiagnostics } from '../../../stores/push-diagnostics-store'
import { getNativePlatform, isNativeApp } from '../../../utils/platform'
import { withTimeout as withTimeoutMs } from '../utils/with-timeout'

/**
 * Every device check setup needs, all non-prompting and time-bound: a check
 * that can't answer in time reads as `unknown`, never as a blocker, and never
 * fires an OS dialog.
 */
export type DeviceProbes = {
  permissions: Omit<DevicePermissionReport, 'v'>
  nativeProbes: { callsChannel: boolean; iosPrecise: boolean }
  appLockSupported: boolean
  appLockOn: boolean
  biometricLabel: string
  webPushSupported: boolean
}

export const PROBE_TIMEOUT_MS = 3_000

export function withTimeout<T>(promise: Promise<T>, fallback: T, ms = PROBE_TIMEOUT_MS): Promise<T> {
  return withTimeoutMs(promise, fallback, ms)
}

export function isWebPushSupported(): boolean {
  if (isNativeApp() || typeof window === 'undefined') return false
  return 'Notification' in window && 'PushManager' in window && 'serviceWorker' in navigator
}

async function probePermission(kind: PermissionKind): Promise<PermState> {
  if (!listApplicablePermissions().includes(kind)) return 'unsupported'
  return withTimeout<PermState>(checkPermission(kind), 'unknown')
}

export async function probeDevice(): Promise<DeviceProbes> {
  const platform = getNativePlatform()
  const native = isNativeApp()
  const android = native && platform === 'android'
  const ios = native && platform === 'ios'
  const webPushSupported = isWebPushSupported()

  const [
    notifications,
    microphone,
    camera,
    location,
    fullScreen,
    battery,
    channel,
    androidPrecision,
    iosAccuracy,
    voipToken,
    biometric,
    appLockOn,
  ] = await Promise.all([
    probePermission('notifications'),
    probePermission('microphone'),
    probePermission('camera'),
    native ? probePermission('location') : Promise.resolve<PermState>('unsupported'),
    android ? withTimeout(canUseFullScreenIntent(), true).then((g) => (g ? 'granted' : 'denied') as PermState) : null,
    android
      ? withTimeout<boolean | null>(isIgnoringBatteryOptimizations(), null).then(
          (ok) => (ok == null ? 'unknown' : ok ? 'granted' : 'prompt') as PermState,
        )
      : null,
    android ? withTimeout(getIncomingCallsChannelStatus(), 'unavailable' as const) : ('unavailable' as const),
    android ? withTimeout(checkAndroidLocationPrecision(), 'unknown' as const) : ('unknown' as const),
    ios ? withTimeout(getLocationAccuracyAuthorization(), 'unavailable' as const) : ('unavailable' as const),
    ios ? withTimeout(getNativeVoipToken(), null) : null,
    native ? withTimeout(getBiometricAvailability(), { available: false, label: 'Face ID or fingerprint' }) : null,
    native ? withTimeout(isBiometricUnlockEnabled(), false) : false,
  ])

  const push = pushDiagnostics.getSnapshot()
  const permissions: DeviceProbes['permissions'] = {
    notifications,
    microphone,
    camera,
    location,
  }
  if (android) {
    permissions.locationPrecise = androidPrecision
    if (fullScreen) permissions.fullScreenIntent = fullScreen
    if (battery) permissions.batteryExempt = battery
    if (channel !== 'unavailable') permissions.callsChannel = channel === 'enabled' ? 'granted' : 'denied'
  }
  if (ios) {
    permissions.locationPrecise = iosAccuracy === 'full' ? 'precise' : iosAccuracy === 'reduced' ? 'approximate' : 'unknown'
    permissions.voipToken = voipToken ? 'registered' : 'missing'
  }
  if (native) {
    permissions.pushToken =
      push.backendStatus === 'success' ? 'registered' : push.backendStatus === 'error' ? 'error' : 'missing'
  } else {
    permissions.webPush = !webPushSupported
      ? 'unsupported'
      : notifications === 'granted'
        ? 'subscribed'
        : 'unsubscribed'
  }

  return {
    permissions,
    nativeProbes: { callsChannel: channel !== 'unavailable', iosPrecise: iosAccuracy !== 'unavailable' },
    appLockSupported: Boolean(biometric?.available),
    appLockOn: Boolean(appLockOn),
    biometricLabel: biometric?.available ? biometric.label : 'Face ID or fingerprint',
    webPushSupported,
  }
}
